<template>
  <div>
    <hr class="mt-1 mb-2">
    <div
      class="d-flex align-items-center"
      style="height: 28px"
    >
      <div
        class="mr-2 checkbox align-self-center"
        @click="checkAll('none')"
      >
        <Checkbox
          :value="groupChecks['none']"
          :pending="groupCheckPending['none']"
          :emit="false"
          style="position: relative; margin-left: -5px"
          class="px-0"
        />
      </div>

      <div
        v-if="checkedOrders.length > 0"
        class="flex-grow-1 d-flex align-items-center"
      >
        <Tippy
          ref="archive-popup"
          trigger="click"
          theme="light"
          arrow
          interactive
        >
          <template #trigger>
            <button
              v-tippy
              :content="$t('shop.cancel')"
              class="btn btn-sm btn-outline-primary icon-btn mx-1"
            >
              <i class="fas fa-ban" />
            </button>
          </template>

          <button
            class="btn btn-primary btn-sm btn-block"
            :disabled="pendingCancel"
            @click="cancelCheckedOrders(false)"
          >
            <i class="fas fa-ban pr-2" />
            {{ $t('shop.cancel') }}
          </button>
        </Tippy>

        <template v-if="!reserveQueue">
          <button
            v-tippy
            :content="$t('general.edit')"
            class="btn btn-sm btn-outline-primary icon-btn mx-1"
            @click="$emit('bulkEdit', checkedOrders)"
          >
            <i class="fas fa-pen" />
          </button>

          <button
            v-tippy
            class="btn btn-outline-primary icon-btn mx-1"
            :content="$t('messages.newMessage')"
            @click="showAddMessage"
          >
            <i class="fas fa-comments" />
            <i class="fas fa-plus xs-add" />
          </button>

          <button
            v-if="anyCategoryAccess"
            v-tippy
            class="btn btn-outline-primary icon-btn mx-1"
            :content="$t('bill.add')"
            @click="showAddBill"
          >
            <i class="fas fa-money-bill-wave" />
            <i class="fas fa-plus xs-add" />
          </button>

          <button
            v-tippy
            :content="$t('shop.moveToReserveQueue')"
            class="btn btn-sm btn-outline-primary icon-btn mx-1"
            :disabled="pendingMove"
            @click="moveToReserveQueueChecked"
          >
            <i class="fas fa-clock" />
          </button>
        </template>

        <template v-else>
          <button
            v-tippy
            :content="$t('shop.promoteFromReserveQueue')"
            class="btn btn-sm btn-outline-primary icon-btn mx-1"
            :disabled="pendingPromote"
            @click="promoteCheckedFromReserveQueue"
          >
            <i class="fas fa-arrow-up" />
          </button>
        </template>

        <div class="pl-2 small text-primary">
          {{ $t('shop.orders') }}: {{ checkedCount }}
        </div>
      </div>
      <div
        v-else
        class="flex-grow-1"
      />
      <template v-if="!editedId">
        <div class="d-flex align-items-center">
          <div class="summary-label mr-3">
            {{ $t('shop.summary') }}:
          </div>
          <div
            class="money-font pr-2"
            style="border-right: 1px solid #ddd"
          >
            <i class="far fa-box" />
            x{{ addSpaces(totalCount) }}
          </div>
          <div
            class="money-font text-right px-2"
          >
            <i class="far fa-money-bill-wave" />
            {{ formatCurrency(totalPrice.price, totalPrice.currency) }}
          </div>
        </div>
      </template>
    </div>
    <hr class="mb-0 mt-2">

    <BModal
      v-model="confirmCancelDelivered"
      hide-footer
      hide-header
    >
      <div class="text-center mb-2">
        <div class="font-weight-bold text-red-500">
          Uwaga!
        </div>
        Usuwasz <span class="font-weight-bold">{{ checkedCount }}</span> zamówień z czego
        <span class="font-weight-bold">{{ checkedDeliveredCount }}</span>
        jest {{ checkedDeliveredCount == 1 ? 'oznaczone' : 'oznaczonych' }} jako dostarczone!
      </div>

      <hr>

      <div class="d-flex">
        <div class="w-50 pr-2">
          <button
            class="btn btn-sm btn-secondary btn-block"
            @click="confirmCancelDelivered = false; $refs['archive-popup'].tippy().hide()"
          >
            {{ $t('general.cancel') }}
          </button>
        </div>
        <div class="w-50 pl-2">
          <button
            class="btn btn-sm btn-danger btn-block"
            :disabled="pendingCancel"
            @click="cancelCheckedOrders(true)"
          >
            Potwierdź usunięcie
          </button>
        </div>
      </div>
    </BModal>

    <BModal
      v-model="showBlockedItems"
      hide-footer
      hide-header
    >
      <div class="text-center mb-2">
        <div class="font-weight-bold text-red-500">
          {{ $t('shop.reserveQueueBlockedTitle') }}
        </div>
        {{ $t('shop.reserveQueueBlockedMessage', { count: blockedItems.length }) }}
      </div>

      <hr>

      <button
        class="btn btn-sm btn-primary btn-block"
        @click="showBlockedItems = false"
      >
        {{ $t('general.close') }}
      </button>
    </BModal>

    <div class="orders-list-body">
      <OrdersFlatList
        v-if="groupBy === 'none'"
        :payers="payers"
        :dates="dates"
        :reserve-queue="reserveQueue"
        :orders="orders['none'] || []"
        :edited-id="editedId"
        :layout="layout"
        :product-sets="productSets"
        :group-by="'none'"
        :pending="listPending['none']"
        :checked="checked"
        @checked="toggle($event, true, 'none')"
        @unchecked="toggle($event, false, 'none')"
        @select="$emit('select', $event)"
      />
      <template
        v-else
      >
        <div class="header-wrapper">
          <OrderHeaderRow
            :columns="groupLayout"
            class="group-header list-padding"
          />
        </div>
        <div
          v-for="(p, index) in visibleOrders"
          :key="p.id"
        >
          <div
            class="checkbox set-row-group d-flex align-items-center"
            :class="{ 'last': index === aggregatedOrders.length - 1 }"
          >
            <div
              class="select-box angle"
              @click="open(p.id)"
            >
              <i
                :class="opened[p.id] ? 'fa-solid fa-angle-down' : 'fa-solid fa-angle-right'"
              />
            </div>
            <div
              class="select-box"
              @click="checkAll(p.id)"
            >
              <Checkbox
                :value="groupChecks[p.id]"
                :pending="groupCheckPending[p.id]"
                :emit="false"
                class="px-0"
              />
            </div>
            <OrderItemRow
              :last="false"
              :columns="groupLayout"
              :order="p"
              :payer="p.payer"
              class="group-row"
              no-checkbox
            />
          </div>
          <div
            v-if="opened[p.id]"
            class="inner-table"
          >
            <OrdersFlatList
              :group-id="p.id"
              :payers="payers"
              :dates="dates"
              :reserve-queue="reserveQueue"
              :orders="orders[p.id] || []"
              :pending="listPending[p.id]"
              :layout="innerLayout"
              :product-sets="productSets"
              :edited-id="editedId"
              :group-by="groupBy"
              :checked="checked"
              @checked="toggle($event, true, p.id)"
              @unchecked="toggle($event, false, p.id)"
              @select="$emit('select', $event)"
            />
          </div>
        </div>
        <InfinityScrollCursor
          v-if="!isBottom"
          loop-check
          :margin="10"
          @request="mainCursor += 30"
        />
        <div
          v-if="pending || !isBottom"
          class="mt-4"
        >
          <Loader />
        </div>
        <div
          v-else-if="aggregatedOrders.length === 0"
          class="text-center py-3 text-secondary"
        >
          <div style="font-size: 1rem">
            <span class="fa-stack fa-2x">
              <i class="far fa-box" />
              <i class="far fa-slash fa-stack-1x" />
            </span>
          </div>
          {{ $t('shop.noOrders') }}
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import InfinityScrollCursor from '@/components/InfinityScrollCursor';
import { addSpaces } from '@/utils/filters';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import OrderHeaderRow from './OrderHeaderRow';
import OrderItemRow from './OrderItemRow';
import OrdersFlatList from './OrdersFlatList';

export default {
  props: {
    aggregatedOrders: {
      type: Array,
      default: () => [],
    },
    payers: {
      type: Array,
      default: () => [],
    },
    productSets: {
      type: Object,
      default: () => ({}),
    },
    layout: {
      type: Array,
      default: () => [],
    },
    dates: {
      type: Object,
      default: () => ({}),
    },
    filter: Object,
    reserveQueue: Boolean,
    editedId: String,
    groupBy: String,
    pending: Boolean,
  },
  data: () => ({
    pendingDownload: false,
    selectedProductSetId: null,
    mainCursor: 30,
    groupChecks: {},
    listPending: {},
    groupCheckPending: {},
    opened: {},
    cursor: {},
    checked: {},
    orders: {},
    showBulkEdit: false,
    pendingCancel: false,
    confirmCancelDelivered: false,
    pendingMove: false,
    pendingPromote: false,
    blockedItems: [],
    showBlockedItems: false,
  }),
  components: {
    InfinityScrollCursor,
    OrderItemRow,
    OrdersFlatList,
    OrderHeaderRow,
  },
  computed: {
    ...mapGetters([
      'payeeId',
      'formatCurrency',
      'anyCategoryAccess',
    ]),
    ...mapGetters('shop', ['singleOrderSchema', 'catalogDetails']),
    checkedOrders() {
      return Object.values(this.checked);
    },
    isBottom() {
      return this.mainCursor >= this.aggregatedOrders.length;
    },
    visibleOrders() {
      return this.aggregatedOrders.slice(0, this.mainCursor);
    },
    innerLayout() {
      if (this.groupBy === 'none') {
        return this.layout;
      }
      if (this.groupBy === 'payers') {
        return this.layout
          .filter((x) => x.key !== 'code'
            && x.key !== 'name'
            && x.type !== 'property'
            && x.type !== 'group');
      }

      if (this.groupBy === 'productSets') {
        return this.layout
          .filter((x) => x.key !== 'productSetName'
            && x.key !== 'vat'
            && x.key !== 'accountingCode'
            && x.key !== 'accountNumber'
            && x.key !== 'timetable'
            && x.type !== 'listing-field'
            && x.type !== 'listing-assignment');
      }

      if (this.groupBy === 'day') {
        return this.layout
          .filter((x) => x.key !== 'day' && x.key !== 'month');
      }

      return this.layout;
    },
    groupLayout() {
      if (this.groupBy === 'none') return this.layout;
      const excludeTimetable = this.groupBy !== 'productSets';
      const groupLayout = this.layout.filter((x) => (!excludeTimetable || x.key !== 'timetable')
      && !this.innerLayout.some((l) => l.key === x.key));

      return [
        ...(groupLayout.length > 0 ? [{ ...groupLayout[0], width: 400 }] : []),
        { key: 'aggregatedCount', width: 80, name: this.$t('shop.offer.aggregatedCount') },

        ...(this.reserveQueue
          ? []
          : [{ key: 'aggregatedPrice', width: 130, name: this.$t('shop.offer.aggregatedPrice') }]),
        ...(groupLayout.length > 1 ? groupLayout.slice(1) : []),
      ];
    },
    checkedDeliveredCount() {
      return this.checkedOrders.reduce((acc, curr) => acc + (curr.delivered > 0 ? 1 : 0), 0);
    },
    checkedCount() {
      return Object.values(this.checked).reduce((acc, curr) => acc + curr.count, 0);
    },
    totalPrice() {
      return this.aggregatedOrders.reduce((acc, curr) => {
        acc.currency = 'pln';
        acc.price += curr.totalValue;
        return acc;
      }, { price: 0, currency: '' });
    },
    totalCount() {
      return this.aggregatedOrders.reduce((acc, curr) => acc + curr.totalCount, 0);
    },
  },
  watch: {
    groupBy() {
      this.mainCursor = 30;
      this.orders = {};
      this.opened = {};

      if (this.groupBy === 'none') {
        this.request('none');
      }
    },
    filter() {
      this.mainCursor = 30;
      this.checked = {};
      this.orders = {};

      if (this.groupBy === 'none') {
        this.request('none');
      } else {
        Object.keys(this.opened)
          .forEach((key) => {
            this.request(key);
          });
      }
    },
    dates() {
      this.mainCursor = 30;
      this.checked = {};
      this.orders = {};

      if (this.groupBy === 'none') {
        this.request('none');
      } else {
        Object.keys(this.opened)
          .forEach((key) => {
            this.request(key);
          });
      }
    },
  },
  methods: {
    ...mapActions('shop', [
      'updateOrders',
      'getOrdersList',
      'getOrders',
      'removeOrdersFromReserveQueue',
      'moveOrdersToReserveQueue',
      'promoteOrdersFromReserveQueue',
    ]),
    addSpaces,
    open(id) {
      this.$set(this.opened, id, !this.opened[id]);
      this.request(id);
    },
    updateItem(order) {
      const co = this.checked[order.id];

      if (!co) return;
      this.$set(co, 'orderStatusId', order.orderStatusId);
      this.$set(co, 'isPaid', order.isPaid);
      this.$set(co, 'delivered', order.delivered ? 1 : 0);
      this.$set(co, 'unitPrice', order.unitPrice);
      this.$set(co, 'count', Number.parseInt(order.count, 10));
      this.$set(co, 'comment', order.comment);
      this.$set(co, 'discountName', order.discountName);

      Object.values(this.orders).forEach((list) => {
        const o = list.find((x) => x.id === order.id);
        if (o) {
          this.$set(o, 'orderStatusId', order.orderStatusId);
          this.$set(o, 'isPaid', order.isPaid);
          this.$set(o, 'delivered', order.delivered ? 1 : 0);
          this.$set(o, 'unitPrice', order.unitPrice);
          this.$set(o, 'count', Number.parseInt(order.count, 10));
          this.$set(o, 'comment', order.comment);
          this.$set(o, 'discountName', order.discountName);
        }
      });
    },
    toggle(o, value) {
      if (value) {
        this.$set(this.checked, o.id, o);
      } else {
        this.$delete(this.checked, o.id);
      }
    },
    showAddMessage() {
      const payers = Object.keys(Object.values(this.checked)
        .reduce((acc, curr) => { acc[curr.payerId] = true; return acc; }, {}))
        .map((x) => this.payers.find((p) => p.id === x));

      this.$store.commit('setSelectedPayers', payers);
      this.$router.push(`/payee/${this.payeeId}/messages/messages?useSelectedPayers=1`);
    },
    showAddBill() {
      const payers = Object.keys(Object.values(this.checked)
        .reduce((acc, curr) => { acc[curr.payerId] = true; return acc; }, {}))
        .map((x) => this.payers.find((p) => p.id === x));

      this.$store.commit('setSelectedPayers', payers);
      this.$router.push(`/payee/${this.payeeId}/bills/form?useSelectedPayers=1`);
    },
    formatDate(date) {
      return moment(date).format('HH:mm DD/MM/YYYY');
    },
    cancelCheckedOrders(force = false) {
      if (this.reserveQueue) {
        this.pendingCancel = true;
        this.removeOrdersFromReserveQueue({
          params: {
            query: {
              catalogId: this.catalogDetails.id,
            },
          },
          data: {
            items: this.checkedOrders.map((x) => ({
              payerId: x.payerId,
              listingId: x.availabilityId,
            })),
          },
        })
          .then(() => {
            this.$emit('refresh');

            Object.keys(this.orders).forEach((groupId) => {
              this.orders[groupId] = this.orders[groupId].filter((x) => !this.checked[x.id]);
            });

            this.checked = {};
          })
          .finally(() => {
            this.pendingCancel = false;
          });
        return;
      }

      const anyDelivered = this.checkedOrders.some((x) => x.delivered > 0);
      if (anyDelivered && !force) {
        this.confirmCancelDelivered = true;
        return;
      }

      this.confirmCancelDelivered = false;

      this.pendingCancel = true;
      this.updateOrders({
        params: {
          query: {
            catalogId: this.catalogDetails.id,
          },
        },
        data: {
          cancelledItems: this.checkedOrders.map((x) => ({
            day: x.day,
            payerId: x.payerId,
            availabilityId: x.availabilityId,
            count: x.count,
          })),
        },
      })
        .then(() => {
          this.$emit('refresh');

          Object.keys(this.orders).forEach((groupId) => {
            this.orders[groupId] = this.orders[groupId].filter((x) => !this.checked[x.id]);
          });

          this.checked = {};
        })
        .finally(() => {
          this.pendingCancel = false;
        });
    },
    moveToReserveQueueChecked() {
      this.pendingMove = true;
      this.moveOrdersToReserveQueue({
        params: {
          query: {
            catalogId: this.catalogDetails.id,
          },
        },
        data: {
          items: this.checkedOrders.map((x) => ({
            payerId: x.payerId,
            listingId: x.availabilityId,
            day: x.day,
            count: x.count,
          })),
        },
      })
        .then(({ data }) => {
          this.$emit('refresh');

          const blocked = data?.blocked || [];
          const blockedKeys = blocked.reduce((acc, curr) => {
            acc[`${curr.payerId}_${curr.listingId}`] = true;
            return acc;
          }, {});

          Object.keys(this.orders).forEach((groupId) => {
            this.orders[groupId] = this.orders[groupId].filter((x) => !this.checked[x.id]
              || blockedKeys[`${x.payerId}_${x.availabilityId}`]);
          });

          this.checked = Object.values(this.checked)
            .filter((x) => blockedKeys[`${x.payerId}_${x.availabilityId}`])
            .reduce((acc, curr) => { acc[curr.id] = curr; return acc; }, {});

          if (blocked.length > 0) {
            this.blockedItems = blocked;
            this.showBlockedItems = true;
          }
        })
        .finally(() => {
          this.pendingMove = false;
        });
    },
    promoteCheckedFromReserveQueue() {
      this.pendingPromote = true;
      this.promoteOrdersFromReserveQueue({
        params: {
          query: {
            catalogId: this.catalogDetails.id,
          },
        },
        data: {
          items: this.checkedOrders.map((x) => ({
            payerId: x.payerId,
            listingId: x.availabilityId,
          })),
        },
      })
        .then(() => {
          this.$emit('refresh');

          Object.keys(this.orders).forEach((groupId) => {
            this.orders[groupId] = this.orders[groupId].filter((x) => !this.checked[x.id]);
          });

          this.checked = {};
        })
        .finally(() => {
          this.pendingPromote = false;
        });
    },
    request(groupKey) {
      if (this.orders[groupKey]) {
        return Promise.resolve(this.orders[groupKey]);
      }

      this.$set(this.listPending, groupKey, true);
      return this.getOrdersList({
        params: {
          query: {
            catalogId: this.catalogDetails.id,
            from: this.dates?.from,
            to: this.dates?.to,
            groupKey,
            reserveQueue: this.reserveQueue,
          },
        },
        data: {
          ...this.filter,
          groupBy: groupKey === 'none' ? 'none' : this.filter.groupBy,
        },
      })
        .then(({ data }) => {
          this.$set(this.orders, groupKey, data);
          return data;
        })
        .finally(() => {
          this.$set(this.listPending, groupKey, false);
        });
    },
    checkAll(key) {
      if (this.groupCheckPending[key]) return;
      const value = !this.groupChecks[key];

      if (key === 'none' && !value) {
        this.checked = {};
        this.groupChecks = {};
        return;
      }

      this.$set(this.groupCheckPending, key, true);

      this.request(key)
        .then((data) => {
          this.$set(this.groupChecks, key, value);
          if (key === 'none') {
            this.aggregatedOrders.forEach((o) => {
              this.$set(this.groupChecks, o.id, true);
            });
          }

          data.forEach((o) => {
            this.toggle(o, value);
          });
        })
        .finally(() => {
          this.$set(this.groupCheckPending, key, false);
        });
    },
  },
  created() {
    if (this.groupBy === 'none') {
      this.request('none');
    }
  },
};
</script>

<style lang="scss" scoped>

.select-box {
  height: 32px;
  width: 32px;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid rgba(100, 100, 100, 0.2);
  cursor: pointer;

  &.angle {
    color: #aaa;
  }

  &:hover {
    background-color: rgba(100, 100, 100, 0.1);
  }
}

.summary-label {
  font-size: 12px;
  line-height: 1.2;
  color: #888;
  text-transform: uppercase;
}

.list-padding {
  padding-left: 64px;
}

.set-row-group {
  height: 32px;
  max-height: 32px;
  border-top: 1px solid #ddd;
  width: min-content;
  min-width: 100%;

  &.last {
    border-bottom: 1px solid #ddd;
  }
}

.text-strikethrough {
  text-decoration: line-through;
}

.orders-list-body {
  height: calc(100vh - 360px);
  overflow: auto;
  padding-bottom: 20px;
}

.payer-row-content {
  padding: 5px 10px 5px 5px;
}

.column-divider {
  border-left: 1px solid rgba(100, 100, 100, 0.1);
}

.money-font {
  font-size: 0.9rem;
}

.inner-table {
  margin-top: 10px;
  margin-bottom: 10px;
  margin-left: 27px;
  border: 1px solid #dddddd;
  border-radius: 10px;
  width: min-content;
}

.inner-header {
  background-color: #f6f6f6;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.group-header {
  background-color: #f6f6f6;
  width: min-content;
  min-width: 100%;
}

.collapse-icon {
  width: 27px;
  text-align: center;
  margin-left: 5px;
  line-height: 30px;
  display: flex;
  align-items: center;

  i {
    background-color: #f6f6f6;
    padding: 5px;
    border-radius: 50%;
    width: 22px;
    min-width: 22px;
    height: 22px;
    line-height: 12px;
    font-size: 15px;
    color: #666;
  }
}

.main-column {
  width: 250px;
  max-width: 250px;
  min-width: 250px;
  font-weight: 0.9rem;
}

.xs-add {
  top: 2px;
  right: 4px;
}

.header-wrapper {
  min-width: min-content;
}

.group-row {
  &:deep(.set-row) {
    border-top: none;
    cursor: default;
  }
  &:deep(.set-row-hover:hover) {
    background-color: initial;
  }
}

.orders-count {
  width: 120px;
  font-weight: 400;
  border-radius: 5px;
  text-align: right;
}

.orders-paid {
  color: $blue;
  text-align: right;
  width: 120px;
}

.orders-unpaid {
  color: $orange;
  text-align: right;
  width: 120px;
}
</style>
